import { useLayoutEffect, useRef, type MouseEventHandler, type ReactNode } from 'react';

export interface ButtonProps {
  /** orno's button variants; `default` is the outlined one. */
  variant?: 'default' | 'acc' | 'ghost' | 'quiet' | 'danger' | 'link' | 'link-sm';
  type?: 'button' | 'submit';
  disabled?: boolean;
  /** Waiting for what the click started: orno draws the mark, says aria-busy and refuses further clicks. */
  busy?: boolean;
  wide?: boolean;
  onClick?: MouseEventHandler<HTMLElement>;
  children: ReactNode;
}

/**
 * `<orno-button>` for React. Two things need adapting:
 *
 * - `busy` is an attribute the element reads, but also the name of one of its methods, so React would assign the
 *   property instead of setting the attribute; it is toggled through a ref.
 * - The element moves its children into the `<button>` it renders. React keeps the children it rendered and would
 *   fail to add or remove one there, so they sit in one stable `<span>` that is moved instead.
 *
 * `variant`, `type` and `wide` are read once, when the element connects: give a button that changes them a new `key`.
 */
export function Button({
  variant = 'default',
  type = 'button',
  disabled = false,
  busy = false,
  wide = false,
  onClick,
  children,
}: ButtonProps) {
  const ref = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    ref.current?.toggleAttribute('busy', busy);
  }, [busy]);

  return (
    <orno-button ref={ref} variant={variant} type={type} disabled={disabled} wide={wide} onClick={onClick}>
      <span className="orno-content">{children}</span>
    </orno-button>
  );
}
